import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'destructive';
  message: string;
  duration?: number;
}

export interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

export interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-dismiss after duration
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss(toast.id), 200); // Wait for exit animation
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onDismiss]);

  // Handle manual dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  // Icon and styling based on type
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          bgClass: 'bg-slate-900/90',
          borderClass: 'border-sky-500/50',
          textClass: 'text-slate-50',
          iconClass: 'text-sky-200',
          iconBgClass: 'bg-sky-500/15 border border-sky-500/30'
        };
      case 'error':
        return {
          icon: ExclamationCircleIcon,
          bgClass: 'bg-slate-900/90',
          borderClass: 'border-rose-500/50',
          textClass: 'text-slate-50',
          iconClass: 'text-rose-200',
          iconBgClass: 'bg-rose-500/15 border border-rose-500/30'
        };
      case 'destructive':
        return {
          icon: TrashIcon,
          bgClass: 'bg-slate-900/90',
          borderClass: 'border-rose-500/60',
          textClass: 'text-slate-50',
          iconClass: 'text-rose-100',
          iconBgClass: 'bg-rose-500/20 border border-rose-500/40'
        };
      case 'info':
      default:
        return {
          icon: InformationCircleIcon,
          bgClass: 'bg-slate-900/90',
          borderClass: 'border-indigo-400/50',
          textClass: 'text-slate-50',
          iconClass: 'text-indigo-200',
          iconBgClass: 'bg-indigo-500/15 border border-indigo-500/30'
        };
    }
  };

  const styles = getToastStyles();
  const Icon = styles.icon;

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.9 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`
        flex items-start gap-3 w-[min(420px,calc(100vw-2rem))] p-4 rounded-xl border shadow-2xl shadow-slate-950/60 backdrop-blur-lg
        ${styles.bgClass} ${styles.borderClass} ${styles.textClass}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${styles.iconBgClass}`}>
        <Icon className={`h-5 w-5 flex-shrink-0 ${styles.iconClass}`} />
      </div>

      <div className="flex-1 text-sm font-semibold text-slate-50">
        {toast.message}
      </div>

      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 text-slate-300 hover:text-white hover:bg-white/5 rounded transition-colors"
        title="Dismiss notification"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </motion.div>
  );
};

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
  position = 'top-right'
}) => {
  const baseOffset = '1rem';
  const safeAreaTop = 'env(safe-area-inset-top, 0px)';
  const safeAreaBottom = 'env(safe-area-inset-bottom, 0px)';
  const safeAreaLeft = 'env(safe-area-inset-left, 0px)';
  const safeAreaRight = 'env(safe-area-inset-right, 0px)';

  const getPositionStyles = (): React.CSSProperties => {
    switch (position) {
      case 'top-left':
        return { top: `calc(${baseOffset} + ${safeAreaTop})`, left: `calc(${baseOffset} + ${safeAreaLeft})` };
      case 'top-center':
        return {
          top: `calc(${baseOffset} + ${safeAreaTop})`,
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'bottom-left':
        return { bottom: `calc(${baseOffset} + ${safeAreaBottom})`, left: `calc(${baseOffset} + ${safeAreaLeft})` };
      case 'bottom-right':
        return { bottom: `calc(${baseOffset} + ${safeAreaBottom})`, right: `calc(${baseOffset} + ${safeAreaRight})` };
      case 'top-right':
      default:
        return { top: `calc(${baseOffset} + ${safeAreaTop})`, right: `calc(${baseOffset} + ${safeAreaRight})` };
    }
  };

  const container = (
    <div className="fixed z-[12000] pointer-events-none" style={getPositionStyles()}>
      <div className="space-y-3 pointer-events-auto">
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              toast={toast}
              onDismiss={onDismiss}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return container;
  }

  return createPortal(container, document.body);
};

// Hook for managing toasts
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newToast: ToastMessage = {
      id,
      duration: toast.duration ?? 3000,
      ...toast
    };

    setToasts(prev => {
      const filtered = prev.filter(existing => !(existing.type === toast.type && existing.message === toast.message));
      return [...filtered, newToast];
    });

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message: string, duration?: number) => {
    return addToast({ type: 'success', message, duration });
  }, [addToast]);

  const destructive = useCallback((message: string, duration?: number) => {
    return addToast({ type: 'destructive', message, duration: duration ?? 4500 });
  }, [addToast]);

  const error = useCallback((message: string, duration?: number) => {
    return addToast({ type: 'error', message, duration: duration ?? 5000 });
  }, [addToast]);

  const info = useCallback((message: string, duration?: number) => {
    return addToast({ type: 'info', message, duration });
  }, [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    success,
    destructive,
    error,
    info
  };
}
